/**
 * Terminal Command Engine
 *
 * Core command parsing and execution logic.
 * Shared between web terminal and CLI tool.
 */

import type {
  CommandResult,
  ITerminalApiClient,
} from './types'

/**
 * Command Engine
 *
 * Parses and executes terminal commands against a virtual file system.
 *
 * @example
 * ```ts
 * const apiClient = new TerminalApiClient()
 * const engine = new CommandEngine(apiClient)
 *
 * const result = await engine.execute('ls /projects', '/')
 * console.log(result.output)
 * ```
 */
export class CommandEngine {
  constructor(private api: ITerminalApiClient) {}

  /**
   * Execute a command string
   *
   * @param input - Raw command input (e.g., "ls -la /projects")
   * @param cwd - Current working directory
   * @returns Command result with output and optional new directory
   */
  async execute(input: string, cwd: string): Promise<CommandResult> {
    const trimmed = input.trim()

    // Handle empty input
    if (!trimmed) {
      return { output: '', exitCode: 0 }
    }

    // Parse command and arguments
    const [command, ...args] = trimmed.split(/\s+/)

    try {
      switch (command) {
        case 'ls':
          return await this.ls(args[0] || cwd)

        case 'cd':
          return this.cd(args[0], cwd)

        case 'cat':
          if (!args[0]) {
            return {
              output: 'cat: missing file operand\nUsage: cat <file>',
              exitCode: 1,
            }
          }
          return await this.cat(args[0], cwd)

        case 'pwd':
          return this.pwd(cwd)

        case 'help':
          return this.help()

        case 'clear':
          return this.clear()

        case 'tree':
          return await this.tree(args[0] || cwd)

        case 'git':
          return await this.git(args, cwd)

        case 'repos':
          return await this.repos(args)

        case 'whoami':
          return await this.whoami()

        case 'fortune':
          return await this.fortune()

        case 'history':
          return await this.careerHistory()

        case 'skills':
          return await this.skills(args)

        case 'exit':
        case 'quit':
          return { output: 'Goodbye! 👋', exitCode: 0 }

        default:
          return {
            output: `Command not found: ${command}\nType 'help' for available commands`,
            exitCode: 1,
          }
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unknown error'
      return { output: `Error: ${message}`, exitCode: 1 }
    }
  }

  /**
   * List directory contents
   */
  private async ls(path: string): Promise<CommandResult> {
    try {
      const entries = await this.api.ls(path)

      if (entries.length === 0) {
        return { output: '', exitCode: 0 }
      }

      const output = entries
        .map((e) => {
          const name = e.type === 'directory' ? `${e.name}/` : e.name
          return name
        })
        .join('\n')

      return { output, exitCode: 0 }
    } catch (error) {
      return {
        output: `ls: cannot access '${path}': No such file or directory`,
        exitCode: 1,
      }
    }
  }

  /**
   * Change directory
   */
  private cd(path: string | undefined, cwd: string): CommandResult {
    if (!path) {
      // cd with no args goes to root
      return { output: '', exitCode: 0, newCwd: '/' }
    }

    // Handle special paths
    if (path === '~' || path === '~/') {
      return { output: '', exitCode: 0, newCwd: '/' }
    }

    if (path === '.') {
      return { output: '', exitCode: 0, newCwd: cwd }
    }

    if (path === '..') {
      const parts = cwd.split('/').filter((p) => p)
      parts.pop()
      const newCwd = '/' + parts.join('/')
      return { output: '', exitCode: 0, newCwd }
    }

    // Resolve relative vs absolute paths
    let newCwd: string
    if (path.startsWith('/')) {
      newCwd = path
    } else {
      newCwd = `${cwd}/${path}`.replace(/\/+/g, '/')
    }

    // Normalize path (remove trailing slash except for root)
    if (newCwd !== '/' && newCwd.endsWith('/')) {
      newCwd = newCwd.slice(0, -1)
    }

    return { output: '', exitCode: 0, newCwd }
  }

  /**
   * Display file contents
   */
  private async cat(path: string, cwd: string): Promise<CommandResult> {
    try {
      // Resolve relative vs absolute path
      const fullPath = path.startsWith('/')
        ? path
        : `${cwd}/${path}`.replace(/\/+/g, '/')

      const contents = await this.api.cat(fullPath)
      return { output: contents, exitCode: 0 }
    } catch (error) {
      return {
        output: `cat: ${path}: No such file or directory`,
        exitCode: 1,
      }
    }
  }

  /**
   * Print working directory
   */
  private pwd(cwd: string): CommandResult {
    return { output: cwd, exitCode: 0 }
  }

  /**
   * Show help
   */
  private help(): CommandResult {
    const output = `
Available Commands:

Navigation:
  ls [path]       List directory contents
  cd <path>       Change directory
  pwd             Print working directory
  tree [path]     Show directory tree

File Operations:
  cat <file>      Display file contents

Git Commands:
  git log         Show commit history (in repo directory)
  repos [opts]    List all GitHub repositories
                  --stars    Sort by stars

Easter Eggs:
  whoami          About Agustin
  fortune         Random insight
  history         Career timeline
  skills [cat]    List skills
                  --web      Web development skills
                  --backend  Backend skills
                  --database Database skills

Other:
  help            Show this help
  clear           Clear screen
  exit            Exit terminal

Examples:
  ls /projects
  cd /projects/agustinmusi-com
  cat /about.md
  git log
  skills --web
`.trim()

    return { output, exitCode: 0 }
  }

  /**
   * Clear screen
   */
  private clear(): CommandResult {
    // ANSI escape code to clear screen and move cursor to top-left
    return { output: '\x1b[2J\x1b[H', exitCode: 0 }
  }

  /**
   * Show directory tree
   */
  private async tree(path: string): Promise<CommandResult> {
    try {
      const entries = await this.api.ls(path)

      if (entries.length === 0) {
        return { output: path, exitCode: 0 }
      }

      let output = path + '\n'

      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i]
        const isLast = i === entries.length - 1
        const prefix = isLast ? '└── ' : '├── '
        const name =
          entry.type === 'directory' ? `${entry.name}/` : entry.name

        output += prefix + name + '\n'

        // For directories, recursively show first level
        if (entry.type === 'directory' && path === '/') {
          try {
            const subEntries = await this.api.ls(`${path}${entry.name}`)
            for (let j = 0; j < subEntries.length; j++) {
              const subEntry = subEntries[j]
              const subIsLast = j === subEntries.length - 1
              const subPrefix = isLast ? '    ' : '│   '
              const subTree = subIsLast ? '└── ' : '├── '
              const subName =
                subEntry.type === 'directory'
                  ? `${subEntry.name}/`
                  : subEntry.name

              output += subPrefix + subTree + subName + '\n'
            }
          } catch {
            // Ignore errors for subdirectories
          }
        }
      }

      return { output: output.trimEnd(), exitCode: 0 }
    } catch (error) {
      return {
        output: `tree: cannot access '${path}': No such directory`,
        exitCode: 1,
      }
    }
  }

  /**
   * Git commands
   */
  private async git(args: string[], cwd: string): Promise<CommandResult> {
    const subcommand = args[0]

    if (!subcommand) {
      return {
        output:
          "usage: git <command>\n\nAvailable commands:\n  log    Show commit history\n  status Show repository status",
        exitCode: 1,
      }
    }

    if (subcommand === 'log') {
      // Extract repo name from current directory
      const repoMatch = cwd.match(/\/repositories\/([^\/]+)/)

      if (!repoMatch) {
        return {
          output:
            'fatal: not in a git repository\n(Use "cd /repositories/<repo>" first)',
          exitCode: 1,
        }
      }

      try {
        const commits = await this.api.getRepoCommits(repoMatch[1])

        if (commits.length === 0) {
          return { output: 'No commits found', exitCode: 0 }
        }

        const output = commits
          .slice(0, 10)
          .map((c) => {
            const shortSha = c.sha.slice(0, 7)
            return `${shortSha} ${c.message} - ${c.author} (${c.date})`
          })
          .join('\n')

        return { output, exitCode: 0 }
      } catch (error) {
        return {
          output: 'fatal: failed to fetch commit history',
          exitCode: 1,
        }
      }
    }

    return {
      output: `git: '${subcommand}' is not a git command`,
      exitCode: 1,
    }
  }

  /**
   * List repositories
   */
  private async repos(args: string[]): Promise<CommandResult> {
    try {
      const repos = await this.api.getRepositories()

      if (repos.length === 0) {
        return { output: 'No repositories found', exitCode: 0 }
      }

      // Sort by stars if --stars flag
      const sortByStars = args.includes('--stars')
      const sorted = sortByStars
        ? [...repos].sort((a, b) => (b.stars || 0) - (a.stars || 0))
        : repos

      const output = sorted
        .map((r) => {
          const stars = r.stars ? ` ⭐ ${r.stars}` : ''
          return `${r.name}${stars}`
        })
        .join('\n')

      return { output, exitCode: 0 }
    } catch (error) {
      return {
        output: 'Error fetching repositories',
        exitCode: 1,
      }
    }
  }

  /**
   * whoami easter egg
   */
  private async whoami(): Promise<CommandResult> {
    try {
      const data = await this.api.getEasterEgg('whoami')
      return { output: data, exitCode: 0 }
    } catch (error) {
      return { output: 'Error loading whoami data', exitCode: 1 }
    }
  }

  /**
   * fortune easter egg
   */
  private async fortune(): Promise<CommandResult> {
    try {
      const fortunes = await this.api.getEasterEgg('fortune')

      if (!Array.isArray(fortunes) || fortunes.length === 0) {
        return { output: 'No fortunes available', exitCode: 1 }
      }

      // Pick random fortune
      const random = fortunes[Math.floor(Math.random() * fortunes.length)]

      return { output: random, exitCode: 0 }
    } catch (error) {
      return { output: 'Error loading fortune data', exitCode: 1 }
    }
  }

  /**
   * Career history easter egg
   */
  private async careerHistory(): Promise<CommandResult> {
    try {
      const timeline = await this.api.getEasterEgg('history')

      if (!Array.isArray(timeline) || timeline.length === 0) {
        return { output: 'No career history available', exitCode: 1 }
      }

      const output = timeline
        .map((entry) => {
          const period = `${entry.startDate} - ${entry.endDate}`
          const tech = entry.technologies
            ? `\n  Tech: ${entry.technologies.join(', ')}`
            : ''

          return `${entry.company} - ${entry.title}\n${period}\n${entry.description}${tech}`
        })
        .join('\n\n')

      return { output, exitCode: 0 }
    } catch (error) {
      return { output: 'Error loading career history', exitCode: 1 }
    }
  }

  /**
   * Skills command
   */
  private async skills(args: string[]): Promise<CommandResult> {
    try {
      const skillsData = await this.api.getSkills()

      // Filter by category if flag provided
      const webFlag = args.includes('--web')
      const backendFlag = args.includes('--backend')
      const databaseFlag = args.includes('--database')

      let output = ''

      if (webFlag) {
        const webSkills = skillsData.categories.frontend
        output = `${webSkills.name}:\n${webSkills.skills.map((s) => `  • ${s}`).join('\n')}`
      } else if (backendFlag) {
        const backendSkills = skillsData.categories.backend
        output = `${backendSkills.name}:\n${backendSkills.skills.map((s) => `  • ${s}`).join('\n')}`
      } else if (databaseFlag) {
        const dbSkills = skillsData.categories.database
        output = `${dbSkills.name}:\n${dbSkills.skills.map((s) => `  • ${s}`).join('\n')}`
      } else {
        // Show all categories
        output = Object.entries(skillsData.categories)
          .map(([_key, category]) => {
            return `${category.name}:\n${category.skills.map((s: string) => `  • ${s}`).join('\n')}`
          })
          .join('\n\n')
      }

      return { output, exitCode: 0 }
    } catch (error) {
      return { output: 'Error loading skills data', exitCode: 1 }
    }
  }
}
